<template>
  <v-card
    class="rounded-lg fill-height"
  >
    <v-card-title class="pa-6 pb-3">
      <v-row
        dense
        align="center"
      >
        <v-col>
          <div class="subtitle-2 grey--text text--grey-100">
            {{ $t('variables.pain.long') }}
          </div>
        </v-col>
        <v-col
          v-if="removable"
          cols="auto"
        >
          <v-btn
            icon
            style="pointer-events: auto"
            @click="$emit('remove')"
          >
            <v-icon small>
              fal fa-times
            </v-icon>
          </v-btn>
        </v-col>
        <v-col
          v-else-if="!showTrendStats"
          cols="auto"
        >
          <v-btn-toggle
            v-model="view"
            color="primary"
            mandatory
          >
            <v-btn
              small
              class="text-none"
              value="visual"
            >
              {{ $t('card.display_option.visual') }}
            </v-btn>
            <v-btn
              small
              class="text-none"
              value="list"
            >
              {{ $t('card.display_option.list') }}
            </v-btn>
          </v-btn-toggle>
        </v-col>
      </v-row>
    </v-card-title>
    <v-card-text
      class="px-6"
      style="height: 85%"
    >
      <v-row
        v-if="!removable && showTrendStats"
        class="pb-3"
      >
        <v-col>
          <v-btn-toggle
            v-model="time"
            color="primary"
            mandatory
          >
            <v-btn
              small
              class="text-none"
              value="today"
            >
              {{ $t('card.team_average_pain.today') }}
            </v-btn>
            <v-btn
              small
              class="text-none"
              value="yesterday"
            >
              {{ $t('card.team_average_pain.yesterday') }}
            </v-btn>
            <v-btn
              small
              class="text-none"
              value="week"
            >
              {{ $t('card.team_average_pain.last_7_days') }}
            </v-btn>
          </v-btn-toggle>
        </v-col>
        <v-col
          cols="auto"
        >
          <v-btn-toggle
            v-model="view"
            color="primary"
            mandatory
          >
            <v-btn
              small
              class="text-none"
              value="visual"
            >
              {{ $t('card.display_option.visual') }}
            </v-btn>
            <v-btn
              small
              class="text-none"
              value="list"
            >
              {{ $t('card.display_option.list') }}
            </v-btn>
          </v-btn-toggle>
        </v-col>
      </v-row>
      <div
        v-if="view === 'visual'"
        class="pt-8"
      >
        <v-row
          justify="center"
        >
          <v-col cols="auto">
            <human-body-front-muscles
              style="height: 300px; width: 190px"
              :stats="calculatedPain"
              type="average"
            />
          </v-col>
          <v-col cols="auto">
            <human-body-back-muscles
              style="height: 300px; width: 190px"
              :stats="calculatedPain"
              type="average"
            />
          </v-col>
        </v-row>
      </div>
      <div
        v-else
        style="width: 100%"
      >
        <p class="text-caption">
          {{ $t('card.team_average_pain.list.description') }}
        </p>
        <v-simple-table
          dense
          fixed-header
          height="325px"
        >
          <thead>
            <tr>
              <th>{{ $t('table_headers.muscle_joint') }}</th>
              <th>{{ $t('pain_type.low') }}</th>
              <th>{{ $t('pain_type.medium') }}</th>
              <th>{{ $t('pain_type.high') }}</th>
            </tr>
          </thead>
          <tbody v-if="Object.keys(painStatsToShow).length === 0">
            <tr>
              <td
                colspan="4"
                class="text-center"
              >
                {{ $t('empty_data.pain_records') }}
              </td>
            </tr>
          </tbody>
          <tbody v-else>
            <tr
              v-for="(painObject, index) in painStatsToShow"
              :key="index"
            >
              <td>
                {{ musclesAndJoints[index.toLowerCase()] ? musclesAndJoints[index.toLowerCase()].label : index }}
              </td>
              <td
                style="width: 15%"
              >
                {{ painObject.count['1'] }}
              </td>
              <td style="width: 15%">
                {{ painObject.count['2'] }}
              </td>
              <td style="width: 15%">
                {{ painObject.count['3'] }}
              </td>
            </tr>
          </tbody>
        </v-simple-table>
      </div>
    </v-card-text>
  </v-card>
</template>
<script>
import HumanBodyBackMuscles from '@/components/human-body/HumanBodyBackMuscles'
import HumanBodyFrontMuscles from '@/components/human-body/HumanBodyFrontMuscles'
import { musclesAndJoints } from '@/data/models'

export default {
  name: 'TeamPainWidget',
  components: {
    HumanBodyBackMuscles,
    HumanBodyFrontMuscles
  },
  props: {
    todayStats: {
      type: Object,
      default: () => ({})
    },
    yesterdayStats: {
      type: Object,
      default: () => ({})
    },
    weekStats: {
      type: Object,
      default: () => ({})
    },
    eventStats: {
      type: [Object, Array],
      default: () => ({})
    },
    removable: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      view: 'visual',
      time: 'today'
    }
  },
  computed: {
    painStatsToShow() {
      let pain = {}

      if (Object.keys(this.eventStats).length) pain = this.eventStats.pain_event
      else if (this.time === 'today') pain = this.todayStats.pain_today
      else if (this.time === 'yesterday') pain = this.todayStats.pain_yesterday
      else pain = this.todayStats.pain_last_7_days

      if (!pain) pain = {}

      const painArray = Object.entries(pain).sort(([, a], [, b]) => b.count.total - a.count.total).filter(([, a]) => a.count.total > 0)

      return Object.fromEntries(painArray)
    },
    calculatedPain() {
      if (!this.painStatsToShow) return {}

      return this.normalizePainStats(this.painStatsToShow)
    },
    showTrendStats() {
      if (Array.isArray(this.eventStats)) return false

      return !Object.keys(this.eventStats || {}).length
    }
  },
  created() {
    this.musclesAndJoints = musclesAndJoints
  },
  methods: {
    normalizePainStats(pain) {
      const keys = Object.keys(pain)
      const calculated = {}

      for (const key of keys) {
        if (!pain[key].incidence) continue

        calculated[key] = pain[key].incidence['1'] + pain[key].incidence['2'] + pain[key].incidence['3'] * 3
      }

      return calculated
    }
  }
}
</script>
